/**
 * @module cercaliagl/Icon
 */

import EventEmitter from 'eventemitter3';
import {
  DEFAULT_MARKER_ANCHOR, DEFAULT_MARKER_OFFSET, DEFAULT_POPUP_ANCHOR, DEFAULT_POPUP_OFFSET, IMG,
} from './globals';
import { getFilename, isOnBase64 } from './util';

/**
 * @typedef {Object} IconOptions
 * @property {string|undefined} [src] Source URL
 * @property {string} [anchor] Anchor `'center' ,  'top' ,  'bottom' , 'left' ,  'right' ,  'top-left' ,  'top-right' ,  'bottom-left' , and  'bottom-right'`. Default `bottom`.
 * @property {Array.<number>|undefined} [offset] Offset
 * @property {string|undefined} [popupAnchor] Anchor `'center' ,  'top' ,  'bottom' , 'left' ,  'right' ,  'top-left' ,  'top-right' ,
 * 'bottom-left' , and  'bottom-right'`. Default `bottom`.
 * @property {Array.<number>|undefined} [popupOffset] Popup offset
 * @property {number} [rotation] Rotation
 * @property {number} [scale] Size multiplier. Default `1`.
 * @property {Array.<number>} [size] Only for SVG icons. Format value `[width, height]`. For example: `[32, 32]`. If not specified, it will use size from `viewBox` attribute from <svg>, else it will use `[24, 24]`.
 * @property {string} [svg] SVG in string format, only those with <path> tag.
 * @property {number} [opacity] Opacity
 * @property {string} [loadedIconImage] Icon image already loaded on maplibre.
 */

/**
 * @type {string}
 */
const DEFAULT_ICON_URL = `${IMG}/marker.png`;

/**
 * @param {Element} svgElem SVG Element
 * @param {Array.<number>} size Size
 * @return {Element}
 */
function ensureSVGWidthHeight(svgElem, size) {
  let width;
  let height;
  if (size) {
    [width, height] = size;
  } else {
    const viewBox = svgElem.getAttribute('viewBox');
    if (viewBox) {
      const match = viewBox.match(/\w+ \w+ (\w+) (\w+)/);
      [, width, height] = match;
    } else {
      width = 24;
      height = 24;
    }
  }
  svgElem.setAttribute('width', `${width}px`);
  svgElem.setAttribute('height', `${height}px`);
  return svgElem;
}

/**
 * @param {string} svgString SVG String
 * @param {number} size Size [width, height].
 * @returns {string}
 */
function svgStringToImageSrc(svgString, size) {
  if (isOnBase64(svgString)) {
    return svgString;
  }
  const parser = new DOMParser();
  const svgXML = parser.parseFromString(svgString, 'image/svg+xml');
  const fixedSvgXML = ensureSVGWidthHeight(svgXML.rootElement ?? svgXML.querySelector(':root'), size);
  const fixedSvgString = new XMLSerializer().serializeToString(fixedSvgXML);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(fixedSvgString)}`;
}

/**
 * Returns the value to be assigned to src.
 * @param {IconOptions} options
 * @returns {string|undefined} Value to be asigned to src
 */
function getSrcFromIconOptions(options) {
  if (options.src !== undefined) return options.src;
  if (options.svg !== undefined) return svgStringToImageSrc(options.svg, options.size);
  if (options.loadedIconImage !== undefined) return undefined;
  return DEFAULT_ICON_URL;
}

/**
 * @classdesc
 *
 * <b>Javascript ES5</b>
 * ```js
 * const icon = new cercaliagl.Icon();
 * ```
 *
 * Class `Icon` is used to provide an icon to a Marker. The icon has meny attributes such as `src`, `anchor`, `offset`, `popupAnchor`, `rotation`, `size`, `svg`.
 * The normal use case is to create an icon specifying the `src` attribute, but it is possible to create an icon specifying SVG, in this last  case
 * `size` must be `[24, 24]`.
 *
 * Example use case using an URL `src` image.
 * ```js
 * const icon = new cercaliagl.Icon({
 *   src: 'https://maps.cercalia.com/gl/v1.0/img/marker.png'
 * });
 * const marker = new cercaliagl.Marker({
 *   icon: icon,
 *   position: new cercaliagl.LonLat(1.431, 42.1431),
 * });
 * map.addMarker(marker);
 * map.setCenter(marker.getPosition());
 * ```
 *
 * <br/>
 * <br/>
 * Example use case creating an SVG:
 *
 * ```js
 * const icon =  new cercaliagl.Icon({
 *   svg: `<svg id="svgicon" version="1.1" xmlns="http://www.w3.org/2000/svg">
       <path fill="#99ff00" d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"></path>
     </svg>`,
 *   size: [24, 24],
 * });
 * const marker = new cercaliagl.Marker({
 *   icon: icon,
 *   position: new cercaliagl.LonLat(1.431, 42.1431),
 * });
 * map.addMarker(marker);
 * map.setCenter(marker.getPosition());
 * ```
 *
 *
 * @extends {EventEmitter}
 * @api
 */
class Icon extends EventEmitter {
  /**
   * @param {IconOptions} options Icon options
   */
  constructor(options) {
    super();
    /**
     * @private
     * @type {string}
     */
    this.src_ = getSrcFromIconOptions(options);

    /**
     * @private
     * @type {string|undefined}
     */
    this.svg_ = options.svg !== undefined ? options.svg : undefined;

    /**
     * @private
     * @type {ol.Size|undefined}
     */
    this.size_ = options.size ? options.size : undefined;

    /**
     * @private
     * @type {string}
     */
    this.anchor_ = options.anchor ? options.anchor : DEFAULT_MARKER_ANCHOR;

    /**
     * @private
     * @type {Array.<number>}
     */
    this.offset_ = options.offset ? options.offset : DEFAULT_MARKER_OFFSET;

    /**
     * @private
     * @type {string}
     */
    this.popupAnchor_ = options.popupAnchor ? options.popupAnchor : DEFAULT_POPUP_ANCHOR;

    /**
     * @private
     * @type {Array.<number>}
     */
    this.popupOffset_ = options.popupOffset ? options.popupOffset : DEFAULT_POPUP_OFFSET;

    /**
     * @private
     * @type {number}
     */
    this.rotation_ = options.rotation ? options.rotation : 0;

    /**
     * @private
     * @type {number}
     */
    this.opacity_ = options.opacity ? options.opacity : 1;

    /**
     * @private
     * @type {number}
     */
    this.scale_ = options.scale ? options.scale : 1;

    /**
     * @private
     * @type {string}
     */
    this.filename_ = this.src_ !== undefined
      ? isOnBase64(this.src_) ? this.src_ : getFilename(this.src_)
      : undefined;

    /**
     * @private
     * @type {number}
     */
    this.width_ = options.size ? options.size[0] : 0;

    /**
     * @private
     * @type {number}
     */
    this.height_ = options.size ? options.size[1] : 0;

    /**
     * @private
     * @type {string}
     */
    // eslint-disable-next-line no-bitwise
    this.svgHash_ = this.svg_ ? `svghash@${this.svg_.split('').reduce((prevHash, currVal) => (((prevHash << 5) - prevHash) + currVal.charCodeAt(0)) | 0, 0)}` : null;

    /**
     * @private
     * @type {string}
     */
    this.loadedIconImage_ = options.loadedIconImage !== undefined ? options.loadedIconImage : undefined;
  }

  /**
   * Get source url.
   * @return {string} Source URL
   * @api
   */
  getSrc() {
    return this.src_;
  }

  /**
   * Icon image already loaded on maplibre
   * @return {string} Return loaded icon image
   */
  getLoadedIconImage() {
    return this.loadedIconImage_;
  }

  /**
   * Get SVG in string format.
   * @return {string}
   * @api
   */
  getSVG() {
    return this.svg_;
  }

  /**
   * Get width
   * @return {number} Width
   * @api
   */
  getWidth() {
    return this.width_;
  }

  /**
   * @private
   * @param {number} width
   */
  setWidth(width) {
    this.width_ = width;
  }

  /**
   * Get height
   * @return {number} Height
   * @api
   */
  getHeight() {
    return this.height_;
  }

  /**
   * @private
   * @param {number} height
   */
  setHeight(height) {
    this.height_ = height;
  }

  /**
   * @param {string} url Url
   * @api
   */
  setSrc(src) {
    this.src_ = src;
    this.filename_ = getFilename(this.src_);
    this.loadedIconImage_ = undefined;
    this.emit('change');
  }

  /**
   * @param {string} loadedIconImage
   */
  setLoadedIconImage(loadedIconImage) {
    this.loadedIconImage_ = loadedIconImage;
    this.src_ = undefined;
    this.filename_ = undefined;
    this.emit('change');
  }

  /**
   * Get anchor icon
   * @return {string} Anchor
   * @api
   */
  getAnchor() {
    return this.anchor_;
  }

  /**
   * Get icon offset.
   * @return {Array.<number>} Offset
   * @api
   */
  getOffset() {
    return this.offset_;
  }

  /**
   * Obtaoin popup anchor
   * @return {string} PopupAnchor
   * @api
   */
  getPopupAnchor() {
    return this.popupAnchor_;
  }

  /**
   * Obtain popup offset
   * @return {Array.<number>} Offset
   * @api
   */
  getPopupOffset() {
    return this.popupOffset_;
  }

  /**
   * Get filename. In SVG case it returns an unique hash (for internal purposes)
   * @return {string|undefined} Filename
   * @api
   */
  getFilename() {
    if (this.svg_) {
      return this.getHashSVG();
    }
    return this.filename_;
  }

  /**
   * Obtaint rotation in degrees
   * @return {number} Rotation in degrees
   * @api
   */
  getRotation() {
    return this.rotation_;
  }

  /**
   * Obtaint size multiplier
   * @return {number} Size multiplier
   * @api
   */
  getScale() {
    return this.scale_;
  }

  /**
   * Set size multiplier
   * @param {number} size Size multiplier
   * @api
   */
  setScale(size) {
    this.scale_ = size;
    this.emit('change');
  }

  /**
   * Set rotation in degrees.
   * @param {number} rotation Rotation in degrees.
   * @api
   */
  setRotation(rotation) {
    this.rotation_ = rotation;
    this.emit('change');
  }

  /**
   * Obtaint opacity
   * @return {number} Opacity
   * @api
   */
  getOpacity() {
    return this.opacity_;
  }

  /**
   * Set opacity.
   * @param {number} opacity Opacity.
   * @api
   */
  setOpacity(opacity) {
    this.opacity_ = opacity;
    this.emit('change');
  }

  /**
   * @param {Array.<number>} offset Offset
   * @api
   */
  setOffset(offset) {
    this.offset_ = offset;
    this.emit('change');
  }

  /**
   * @return {string}
   */
  getHashSVG() {
    return this.svgHash_;
  }

  /**
   * Icon size. If is `svg` this size should be `[24, 24]`.
   * @return {Array.<number>} Return icon size
   */
  getSize() {
    return this.size_;
  }
}

/**
 * @return {Icon} Icon default.
 */
export function createDefault() {
  return new Icon({
    src: DEFAULT_ICON_URL,
    anchor: DEFAULT_MARKER_ANCHOR,
    offset: DEFAULT_MARKER_OFFSET,
    popupAnchor: DEFAULT_POPUP_ANCHOR,
    popupOffset: DEFAULT_POPUP_OFFSET,
  });
}

export default Icon;
